/**
 * Services — a browsable list of the workshop's catalog service
 * categories, mirroring the Inventory layout (header + sticky search +
 * rows). Price transparency left the public catalogue (V0228): a
 * category no longer carries a price at all — the manager types the
 * real price and duration per work order — so this list shows the
 * category name and key only. VAT is no longer a catalogue concept
 * (V0230): it's set once, globally, in management's settings, and only
 * ever shown on invoice lines. Searchable by name / key.
 */
import { useMemo, useState } from 'react'
import { useCatalogServices } from '@/hooks/useStaff'
import { serviceLabel } from '@/apis/staffApis'
import { useI18n } from '@/i18n/TranslationContext'
import type { ServiceTypeResponse } from '@/apis/types'

export function ServicesRoute() {
  const { t } = useI18n()
  const [search, setSearch] = useState('')
  const { data, isLoading, isError } = useCatalogServices()

  const services = useMemo(() => {
    const all = (data?.content ?? []).filter((s) => s.isActive)
    const q = search.trim().toLowerCase()
    if (!q) return all
    return all.filter(
      (s) => serviceLabel(s).toLowerCase().includes(q) || s.key.toLowerCase().includes(q),
    )
  }, [data, search])

  return (
    <section aria-label={t.services.title}>
      <h1 className="mb-1.5 font-display text-26 font-extrabold tracking-mk-neg5 text-glass-text">
        {t.services.title}
      </h1>
      <p className="mb-px14 text-13 tracking-mk-p1 text-glass-text-muted">{t.services.subtitle}</p>

      {/* Sticky so the search stays reachable while the list scrolls — same
          recipe as InventoryRoute (pins under the sticky topbar). */}
      <div className="sticky top-[60px] z-10 -mx-6 mb-3 bg-glass-base px-6 pb-2 pt-1">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t.services.search}
          className="w-full rounded-sm border border-glass-border bg-glass-fill px-3.5 py-px11 text-13 text-glass-text placeholder:text-glass-text-muted focus:border-accent focus:outline-none"
        />
      </div>

      {isLoading ? (
        <div role="status" className="py-12 text-center text-sm text-glass-text-muted">{t.home.loading}</div>
      ) : isError ? (
        <div role="alert" className="py-12 text-center text-sm text-glass-text-muted">{t.services.loadError}</div>
      ) : services.length === 0 ? (
        <div className="rounded-md border border-glass-border bg-glass-fill p-px18 text-center text-sm text-glass-text-muted">
          {t.services.empty}
        </div>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {services.map((s) => (
            <ServiceRow key={s.id} service={s} />
          ))}
        </ul>
      )}
    </section>
  )
}

function ServiceRow({ service }: { readonly service: ServiceTypeResponse }) {
  return (
    <li className="flex items-center gap-3 rounded-md border border-glass-border bg-glass-fill p-px14">
      <div className="min-w-0 flex-1">
        <div className="truncate text-13-5 font-bold text-glass-text">{serviceLabel(service)}</div>
        <div className="mt-0.5 text-11-5 text-glass-text-muted">{service.key}</div>
      </div>
    </li>
  )
}
